/**
 * ChangePinScreen
 *
 * Available from /settings or inbox header when logged in.
 * Requires:
 *   - current PIN (verifies by attempting decrypt of the local backup)
 *   - new PIN x2
 *
 * Re-encrypts the mnemonic with the new PIN, uploads to server,
 * refreshes local backup.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Frame, Header, Footer, Btn, Box, FieldLabel, Cursor,
} from '../components/Frame';
import { useStore } from '../lib/store';
import { callFn } from '../lib/firebase';
import {
  isValidPin,
  isPinStrong,
  encryptMnemonic,
  decryptMnemonic,
  saveLastSigil,
  saveUnlockMode,
} from '../lib/pinCrypto';
import { isValid } from '../lib/mnemonic';

export default function ChangePinScreen() {
  const navigate = useNavigate();
  const { sigil, mnemonic } = useStore();
  const sigilLower = (sigil || '').toLowerCase();

  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [stage, setStage] = useState('idle');
  const [success, setSuccess] = useState(false);

  async function onChange() {
    setErr(null);
    // Il segreto attuale puo' essere un PIN (6 cifre) o una passphrase (Premium):
    // non lo vincoliamo al formato PIN — sara' il decrypt a validarlo.
    if (currentPin.length < 6) { setErr('inserisci il PIN o la passphrase attuale'); return; }
    if (!isValidPin(newPin)) { setErr('nuovo PIN: 6 cifre'); return; }
    if (!isPinStrong(newPin)) {
      setErr('nuovo PIN troppo debole. Evita sequenze o ripetizioni.');
      return;
    }
    if (newPin !== confirmPin) { setErr('i due nuovi PIN non corrispondono'); return; }
    if (currentPin === newPin) { setErr('il nuovo PIN deve essere diverso'); return; }
    if (!sigil || !mnemonic) {
      setErr('sessione non valida. Sblocca prima.');
      return;
    }

    setBusy(true);
    try {
      // 1. Verifica PIN attuale: il blob NON e' piu' in localStorage,
      //    fetch dal server (passa per il rate-limit 10/h, 30/24h).
      setStage('verifying');
      const fetchRes = await callFn('fetchBackup')({ sigilLower });
      const fetchData = fetchRes?.data || {};
      if (fetchData.rateLimited) {
        setErr(`Troppi tentativi. Riprova tra ${fetchData.retryAfterMinutes || 60} minuti.`);
        return;
      }
      if (!fetchData.ok || !fetchData.found) {
        setErr('Backup non trovato sul server. Per ri-impostare il PIN serve recovery con 24 parole.');
        return;
      }
      const tryDecrypt = await decryptMnemonic({
        pin: currentPin,
        sigilLower,
        blob: fetchData.blob,
        salt: fetchData.salt,
        nonce: fetchData.nonce,
        argon2: fetchData.argon2 || null,
      });
      if (!tryDecrypt || !isValid(tryDecrypt)) {
        setErr('PIN attuale sbagliato');
        return;
      }

      // 2. Encrypt with new PIN
      setStage('encrypting');
      const bundle = await encryptMnemonic({
        pin: newPin,
        sigilLower,
        mnemonic,
      });

      // 3. Upload to server
      setStage('uploading');
      const fn = callFn('setBackup');
      const res = await fn({
        sigilLower,
        blob: bundle.blob,
        salt: bundle.salt,
        nonce: bundle.nonce,
        argon2: bundle.argon2,
      });
      const payload = res.data;
      if (!payload?.ok) {
        throw new Error(
          `server: ${payload?.error || 'sconosciuto'}` +
          (payload?.stage ? ` (${payload.stage})` : '')
        );
      }

      // 4. Aggiorna solo il sigil locale (no blob localmente)
      saveLastSigil(sigil);
      saveUnlockMode(sigil, 'pin'); // tornato al PIN: il prossimo sblocco usa il tastierino

      setSuccess(true);
      setStage('done');
      setTimeout(() => navigate('/inbox'), 1500);
    } catch (e) {
      console.error('change PIN failed', e);
      setErr(e?.message || 'errore sconosciuto');
      setStage('idle');
    } finally {
      setBusy(false);
    }
  }

  if (success) {
    return (
      <Frame statusLeft="PIN_CHANGED" statusRight="5G">
        <Header title="// PIN AGGIORNATO" />
        <Box variant="success" className="text-[16px]">
          ✓ PIN aggiornato con successo. Useralo dal prossimo unlock.
        </Box>
      </Frame>
    );
  }

  return (
    <Frame statusLeft="CHANGE_PIN" statusRight="5G">
      <Header
        title="// CAMBIA PIN"
        crumb="◀ inbox"
        onCrumbClick={() => navigate('/inbox')}
      />

      <p className="text-[16px] text-ink-dim mb-4">
        sostituisce il PIN attuale. il backup remoto viene aggiornato
        contestualmente.
      </p>

      <div className="space-y-3">
        <div>
          <FieldLabel>&gt; PIN O PASSPHRASE ATTUALE</FieldLabel>
          <input
            type="password"
            autoComplete="current-password"
            value={currentPin}
            onChange={(e) => setCurrentPin(e.target.value)}
            disabled={busy}
            className="w-full bg-black border border-ink-dim text-ink font-mono text-[18px] px-3 py-2 rounded-lg focus:border-ink focus:outline-none"
            placeholder="segreto attuale"
          />
        </div>

        <div>
          <FieldLabel>&gt; NUOVO PIN</FieldLabel>
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/[^0-9]/g, ''))}
            disabled={busy}
            className="w-full bg-black border border-ink-dim text-ink text-glow font-mono text-[20px] tracking-[0.3em] px-3 py-2 focus:border-ink focus:outline-none text-center"
            placeholder="••••••"
          />
        </div>

        <div>
          <FieldLabel>&gt; RIPETI NUOVO PIN</FieldLabel>
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={confirmPin}
            onChange={(e) => setConfirmPin(e.target.value.replace(/[^0-9]/g, ''))}
            disabled={busy}
            className="w-full bg-black border border-ink-dim text-ink text-glow font-mono text-[20px] tracking-[0.3em] px-3 py-2 focus:border-ink focus:outline-none text-center"
            placeholder="••••••"
          />
        </div>

        {err && (
          <Box variant="danger" className="text-[16px]">
            <span className="text-red-500">✕</span> {err}
          </Box>
        )}

        {busy && (
          <Box variant="warn" className="text-[16px]">
            {stage === 'verifying' && (<>verifica PIN attuale<Cursor /></>)}
            {stage === 'encrypting' && (<>cifratura<Cursor /></>)}
            {stage === 'uploading' && (<>aggiornamento server<Cursor /></>)}
          </Box>
        )}
      </div>

      <Footer>
        <Btn
          variant="primary"
          onClick={onChange}
          disabled={busy || currentPin.length < 6 || newPin.length !== 6 || confirmPin.length !== 6}
        >
          {busy ? '...' : 'Aggiorna PIN ▶'}
        </Btn>
      </Footer>
    </Frame>
  );
}
